import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { operations, selectors as lmsSelectors } from 'store/lmsManager'
import { selectors as contextSelectors } from 'auth/stores/userContext'
import { selectors as licenseSelectors } from 'store/licenseManager'
import { CourseManagerType } from 'layers/content/v2/Hocs/CourseManagerGetter'
import { FlaggedCourseManagerGetter as CourseManagerGetter } from 'layers/content/v2/Hocs/FlaggedCourseManagerGetter'
import { navigationEvent } from 'layers/navigation/store/operations'
import {
  isPreviewMode,
  transformInstructorDataToOldShape,
  isAdmin,
  checkIfLessonIsComplete,
  findNode,
  formatDate,
  formatSchoolName,
  extractTextFromField,
} from 'utils/highschoolHelpers'
import {
  isHighSchoolUrl,
  isK5Url,
  isMiddleSchoolUrl,
} from 'utils/productHelpers'
import { programs } from 'layers/content/v2/hooks/useCourseManager'
import MarkCompleteSection from './component'
import {
  getCourseNameFromCourseTree,
  filterUserContextForCourse,
} from './helpers'
import {
  PROGRAM_TRAINING_LESSON_TYPE,
  SCHOOLWIDE_TRAINING_LESSON_TYPE,
  CURRICULUM_TRAINING_LESSON_TYPE,
  MARK_COMPLETE_ID,
  MARK_COMPLETE_HASH,
} from './constants'

function MarkCompleteSectionContainer({
  // TODO: pull in props for the schoolwide training
  completionButtonLabelDefaultAdmin,
  completionButtonLabelDefaultEducator,
  completionDateDefault,
  completionDateDone,
  completionHeader,
  completionLabelDone,
  completionSchool,
  courseManager,
  courseNodeId,
  fetchInstructorProgramFlexGet,
  fetchOrCreateInstructorProgramFlex,
  hsActiveSites,
  instructorData,
  navigationEvent,
  nextStepButtonLabel,
  nextStepButtonLabelRef,
  nextStepHeader,
  nextStepHeaderRef,
  nextStepRoute,
  nextStepRouteRef,
  onMarkComplete,
  userContext,
  userContextManager,
}) {
  const [lessonType, setLessonType] = useState('')
  const [registrationId, setRegistrationId] = useState('')
  const [leafNodeId, setLeafNodeId] = useState('')
  const [isAlreadyComplete, setIsAlreadyComplete] = useState(false)
  const [schoolName, setSchoolName] = useState(null)
  const [
    completionCommentaryComplete,
    setCompletionCommentaryComplete,
  ] = useState(null)
  const [completionCommentaryTodo, setCompletionCommentaryTodo] = useState(null)
  const [
    shoulDisplayCompletionDetails,
    setShoulDisplayCompletionDetails,
  ] = useState(false)

  useEffect(() => {
    if (lessonType && lessonType !== SCHOOLWIDE_TRAINING_LESSON_TYPE) {
      setShoulDisplayCompletionDetails(false)
      return
    }

    setShoulDisplayCompletionDetails(true)

    const currentSite = hsActiveSites.find(
      site => site?.id === userContext.hsActiveSiteId,
    )

    let completionTodo = ''

    setSchoolName(currentSite?.name)
    if (!!completionSchool && schoolName) {
      completionTodo = `${completionSchool} ${formatSchoolName(schoolName)}\n`
    }

    if (completionDateDefault) {
      completionTodo += completionDateDefault
    }

    setCompletionCommentaryTodo(completionTodo)
  }, [lessonType, hsActiveSites, userContext])

  useEffect(() => {
    // fake the scroll to hash because we're using a navigation event, not a real a href attribute:
    if (window?.location?.hash === MARK_COMPLETE_HASH) {
      setTimeout(() => {
        const markCompleteSection = document.getElementById(MARK_COMPLETE_ID)
        markCompleteSection?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        })
      }, 100) // add a 100ms delay to allow the page to fully render before scrolling
    }
  }, [])

  useEffect(() => {
    function updateLessonData(instructorData) {
      setRegistrationId(instructorData?.registration_id)

      const { isComplete, date } = checkIfLessonIsComplete(
        instructorData?.lesson_nodes || [],
        courseNodeId,
      )

      setIsAlreadyComplete(isComplete)

      let completionComentary = ''

      if (!!completionSchool && !!schoolName) {
        completionComentary = `${completionSchool} ${formatSchoolName(
          schoolName,
        )}\n`
      }

      if (!!completionDateDone && !!date) {
        completionComentary += `${completionDateDone} ${formatDate(date)}.`
      }

      setCompletionCommentaryComplete(completionComentary)
    }

    if (
      (lessonType && lessonType !== SCHOOLWIDE_TRAINING_LESSON_TYPE) ||
      (lessonType === SCHOOLWIDE_TRAINING_LESSON_TYPE && !schoolName)
    ) {
      return
    }

    if (!courseNodeId) {
      console.warn(
        'courseNodeId must be set in order to use MarkCompleteSectionContainer',
      )
      return
    }
    // Determine which course tree to use based on the current URL/program
    let courseTree = null
    if (isHighSchoolUrl()) {
      courseTree = courseManager?.courseTrees?.highschool
    } else if (isMiddleSchoolUrl()) {
      courseTree = courseManager?.courseTrees?.['6-8_training_tier1']
    } else if (isK5Url()) {
      courseTree = courseManager?.courseTrees?.['k-5_training_tier1']
    }

    if (!courseTree) {
      return
    }
    if (!instructorData) {
      return
    }
    const transformedInstructorData = transformInstructorDataToOldShape(
      instructorData,
    )

    const courseTreeNode = findNode(courseTree, courseNodeId)
    if (!courseTreeNode) {
      console.warn(
        `courseNodeId ${courseNodeId} not found in the current program's course tree`,
      )
      return
    }
    setLessonType(courseTreeNode.type)
    setLeafNodeId(courseTreeNode.leafNodeId)
    switch (courseTreeNode.type) {
      // these two training types use the same registration_id:
      case PROGRAM_TRAINING_LESSON_TYPE:
      case CURRICULUM_TRAINING_LESSON_TYPE:
        updateLessonData(transformedInstructorData?.educator)
        break
      case SCHOOLWIDE_TRAINING_LESSON_TYPE:
        updateLessonData(transformedInstructorData?.site)
        break
    }
  }, [courseManager, schoolName, instructorData])

  async function handleToggleComplete(isComplete) {
    if (isComplete) {
      // Determine the course tree name and corresponding course name
      let courseTreeName = null
      let programKey = null

      if (isHighSchoolUrl()) {
        courseTreeName = 'highschool'
        programKey = 'highSchool'
      } else if (isMiddleSchoolUrl()) {
        courseTreeName = '6-8_training_tier1'
        programKey = 'mspDigital'
      } else if (isK5Url()) {
        courseTreeName = 'k-5_training_tier1'
        programKey = 'k5SelDigital'
      }

      // Get the course name from the course tree name
      const courseName = getCourseNameFromCourseTree(courseTreeName)

      // Filter the userContext to include only the specific course
      const filteredUserContext = filterUserContextForCourse(
        userContext,
        courseName,
        programKey,
      )

      // Base64 encode the filtered userContext
      const userContextB64 = btoa(JSON.stringify(filteredUserContext))

      const payload = {
        registrationId,
        leafNodeId: leafNodeId || courseNodeId,
        userContextB64,
      }
      // TODO: we may want to support other lessonTypes in this component in the future:
      if (
        lessonType === PROGRAM_TRAINING_LESSON_TYPE ||
        lessonType === SCHOOLWIDE_TRAINING_LESSON_TYPE ||
        lessonType === CURRICULUM_TRAINING_LESSON_TYPE
      ) {
        payload.progress = 1
      }
      await onMarkComplete(payload)
      // refresh the instructor data using the appropriate method after the attempt data is updated:
      if (isHighSchoolUrl()) {
        fetchOrCreateInstructorProgramFlex(
          userContext.hsActiveSiteId,
          userContext,
        )
      } else {
        fetchInstructorProgramFlexGet()
      }
    }
  }

  function handleClickNext() {
    navigationEvent(extractTextFromField(nextStepRoute, nextStepRouteRef))
  }

  // should only be able to mark button complete in instance mode
  if (isPreviewMode()) {
    return null
  }

  return (
    <MarkCompleteSection
      buttonText={
        isAdmin(userContext)
          ? completionButtonLabelDefaultAdmin
          : completionButtonLabelDefaultEducator
      }
      completeText={completionLabelDone}
      completionCommentaryComplete={completionCommentaryComplete}
      completionCommentaryTodo={completionCommentaryTodo}
      handleClickNext={handleClickNext}
      handleToggleComplete={handleToggleComplete}
      hasPermissionsToComplete={
        // only admins can mark schoolwide training as complete
        // all other trainings, anyone can mark complete
        lessonType !== SCHOOLWIDE_TRAINING_LESSON_TYPE || isAdmin(userContext)
      }
      id={MARK_COMPLETE_ID}
      isAlreadyComplete={isAlreadyComplete}
      nextSectionButtonText={extractTextFromField(
        nextStepButtonLabel,
        nextStepButtonLabelRef,
      )}
      nextSectionPromptText={extractTextFromField(
        nextStepHeader,
        nextStepHeaderRef,
      )}
      promptText={completionHeader}
      shoulDisplayCompletionDetails={shoulDisplayCompletionDetails}
    />
  )
}

const mapStateToProps = state => {
  const activeSites = licenseSelectors?.selectActiveSites(state) || []
  let hsActiveSites = []

  if (Array.isArray(activeSites)) {
    hsActiveSites = activeSites.filter(sites =>
      sites?.licenses?.some(license =>
        license?.product?.programCodes?.includes('HSP'),
      ),
    )
  }

  // Determine which instructor data to use based on the current URL
  let instructorData = null
  if (isHighSchoolUrl()) {
    instructorData = lmsSelectors.selectHsInstructorData(state)
  } else if (isMiddleSchoolUrl()) {
    instructorData = lmsSelectors.selectMiddleSchoolInstructorData(state)
  } else if (isK5Url()) {
    instructorData = lmsSelectors.selectk5InstructorData(state)
  }

  return {
    hsActiveSites,
    instructorData,
    // TODO LEARN-20166 Update hs to use useUserCOntext
    userContext: contextSelectors.selectProgramContext(state),
    userContextManager: state.userContextManager,
  }
}

const mapDispatchToProps = {
  onMarkComplete: operations.makeAttemptV2,
  navigationEvent,
  fetchOrCreateInstructorProgramFlex:
    operations.fetchOrCreateInstructorProgramFlex,
  fetchInstructorProgramFlexGet: operations.fetchInstructorProgramFlexGet,
}

MarkCompleteSectionContainer.propTypes = {
  completionButtonLabelDefaultAdmin: PropTypes.string,
  completionButtonLabelDefaultEducator: PropTypes.string,
  completionDateDefault: PropTypes.string,
  completionDateDone: PropTypes.string,
  completionHeader: PropTypes.string,
  completionLabelDone: PropTypes.string,
  completionSchool: PropTypes.string,
  courseManager: CourseManagerType,
  courseNodeId: PropTypes.string,
  fetchInstructorProgramFlexGet: PropTypes.func,
  fetchOrCreateInstructorProgramFlex: PropTypes.func,
  hsActiveSites: PropTypes.array,
  instructorData: PropTypes.object,
  navigationEvent: PropTypes.func,
  nextStepButtonLabel: PropTypes.string,
  nextStepButtonLabelRef: PropTypes.object,
  nextStepHeader: PropTypes.string,
  nextStepHeaderRef: PropTypes.object,
  nextStepRoute: PropTypes.string,
  nextStepRouteRef: PropTypes.object,
  onMarkComplete: PropTypes.func,
  userContext: PropTypes.object,
  userContextManager: PropTypes.object,
}

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(
  CourseManagerGetter(MarkCompleteSectionContainer, {
    programResolver: () => {
      if (isHighSchoolUrl()) {
        return programs.highSchool
      }
      // for k8, we don't need the curriculum course trees, only the trainings
      if (isK5Url()) {
        return programs.elementaryTrainingOnly
      }
      if (isMiddleSchoolUrl()) {
        return programs.middleSchoolTrainingOnly
      }
      return programs.none
    },
  }),
)
